import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { migrate, TEN_FIELDS } from "@rental/shared";
import { portal } from "./lib/api.js";
import { C, fontStack } from "./theme.js";
import { TenantPortal } from "./App.jsx";

/**
 * מעטפת האזור האישי.
 *
 * ההבדל המהותי מגרסת הארטיפקט: כאן הרכיב מקבל אך ורק את מה שהשרת בנה
 * לדירה הזו. בגרסה הקודמת הוא קיבל את כל אובייקט הנתונים — כלומר כל
 * הדיירים האחרים, צילומי תעודות הזהות שלהם ופרטי הבנק של בעל הנכס היו
 * זמינים בדפדפן של כל דייר, גם אם המסך לא הציג אותם.
 */

/* migrate() מסתיים תמיד ב-syncApartments, שממזג לכל נכס את currentTenancy
   (מי גר כאן *היום*) — נכון לחשבון מלא עם כמה נכסים ותקופות, אבל שגוי כאן:
   הפלח הזה מכיל תמיד תקופה אחת בדיוק, זו שהשרת כבר בחר (buildTenantSlice/
   portalTenancy), גם כשהיא עתידית. אצל דייר שטרם הגיע יום כניסתו,
   currentTenancy מחזירה null ו-syncApartments מאפס את apt.tenant/phone/
   guarantors/contracts בחזרה לריק — הדייר נכנס לאזור האישי שלו ורואה
   כותרת בלי שם. התיקון: אחרי migrate, למזג בחזרה את שדות התקופה
   מ-doc.tenancies[0] בדיוק כמו ש-buildTenantSlice עשה בשרת. */
function withResolvedTenancy(doc) {
  const ten = doc.tenancies?.[0];
  const apt = doc.apartments?.[0];
  if (!ten || !apt) return doc;
  const patch = Object.fromEntries(TEN_FIELDS.map((k) => [k, ten[k]]));
  return { ...doc, apartments: [{ ...apt, ...patch }] };
}

export default function TenantApp({ initial, onExit }) {
  const [state, setState] = useState(() => ({
    doc: withResolvedTenancy(migrate(initial.doc)),
    apartmentId: initial.apartmentId,
    impersonating: !!initial.impersonating,
  }));
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const r = await portal.me();
      setState({
        doc: withResolvedTenancy(migrate(r.doc)),
        apartmentId: r.apartmentId,
        impersonating: !!r.impersonating,
      });
      setError("");
    } catch (err) {
      // 403 = בעל הנכס סגר את האזור האישי בזמן שהדייר היה מחובר.
      setError(err.message);
    }
  }, []);

  // רענון כשחוזרים ללשונית — הדייר משאיר את הדף פתוח בטלפון לימים.
  useEffect(() => {
    const onVisible = () => document.visibilityState === "visible" && refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [refresh]);

  const handleReading = useCallback(
    async (payload) => {
      const r = await portal.submitReading(payload);
      await refresh();
      return r;
    },
    [refresh],
  );

  const handlePaymentReport = useCallback(
    async (payload) => {
      const r = await portal.reportPayment(payload);
      await refresh();
      return r;
    },
    [refresh],
  );

  /**
   * בקשת הארכת חוזה וביטולה. השרת כותב את הבקשה למסמך ורושם התראה
   * לבעל הנכס; המסך מתרענן מיד אחרי כדי שהסטטוס יוצג מהמסמך עצמו.
   */
  const handleExtension = useCallback(
    async ({ action, to, note, requestId }) => {
      const r = action === "withdraw"
        ? await portal.withdrawExtension(requestId)
        : await portal.requestExtension(to, note);
      await refresh();
      return r;
    },
    [refresh],
  );

  /**
   * פעולות על הערבים — הוספה/עדכון, חתימה על המסך ומחיקה. setData כאן הוא
   * no-op במכוון, ולכן עורך הערבים מקבל את המסלול הזה במקומו; בלעדיו
   * "החתמה כאן" נראתה עובדת ולא שמרה דבר.
   */
  const handleGuarantor = useCallback(
    async (op) => {
      const r = op.action === "sign"
        ? await portal.signGuarantor(op.guarantorId, op.signature)
        : op.action === "delete"
          ? await portal.deleteGuarantor(op.guarantorId)
          : await portal.saveGuarantor(op.guarantor);
      await refresh();
      return r;
    },
    [refresh],
  );

  if (error) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center px-4"
        style={{ background: C.surface, color: C.ink, fontFamily: fontStack }}>
        <div className="w-full max-w-sm rounded-2xl border p-6 text-center"
          style={{ background: C.card, borderColor: C.line }}>
          <AlertTriangle size={24} style={{ color: C.ochre, margin: "0 auto" }} />
          <h1 className="text-lg font-bold mt-2">{error}</h1>
          <button onClick={onExit} className="mt-4 w-full rounded-xl py-2.5 text-sm"
            style={{ border: `1px solid ${C.line}` }}>
            יציאה
          </button>
        </div>
      </div>
    );
  }

  const apt = state.doc.apartments.find((a) => a.id === state.apartmentId);
  if (!apt) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center"
        style={{ background: C.surface, color: C.muted }}>
        <Loader2 className="animate-spin" size={22} />
      </div>
    );
  }

  return (
    <TenantPortal
      apt={apt}
      data={state.doc}
      scope="tenant"
      locked
      impersonating={state.impersonating}
      onReading={handleReading}
      onPaymentReport={handlePaymentReport}
      onExtension={handleExtension}
      onGuarantor={handleGuarantor}
      onExit={onExit}
      /* אין setData ואין run: הדייר לא כותב למסמך ישירות. כל שינוי עובר
         דרך נקודות קצה צרות בשרת. */
      setData={() => {}}
      run={() => ({ logs: [], focus: null, data: state.doc })}
    />
  );
}
